import React, {useEffect, useMemo, useRef, useState} from "react";
import html2canvas from "html2canvas";
import {makeGetRequest, makePatchRequest} from "Services/ServicesUtils";
import {API_AUDITS} from "Models/Audit/AuditEndpoints";
import {GSPSGoogleMap} from "Components/Map/GSPSGoogleMap";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {AuditMapSelectedIssues} from "Pages/Audits/AuditMapSelectedIssues";
import {AuditMapSelectedLines} from "Pages/Audits/AuditMapSelectedLines";
import {AuditMapSelectedAreas} from "Pages/Audits/AuditMapSelectedAreas";
import {EmailPopup} from "Components/Common/EmailPopup";
import {ReviewerInformation} from "Pages/Audits/AuditReviewer";
import {AuditQuestions} from "Pages/Audits/AuditQuestions";
import {initMediaSelector} from "./AuditMedia";
import {AuditorInformation} from "./AuditorInformation";
import GSPSAnnotationModal from "Components/Map/GSPSAnnotationModal";
import GSPSBreadCrumbs from "Components/Common/GSPSBreadCrumbs";
import {Divider, MenuItem, Select} from "@mui/material";
import {AuditContainer} from "./AuditCreator.styles";
import {filterAndIndexItems} from "Utils/helpers";
import {SVPMapFilter} from "Components/Map/SVPMapFilter";
import SVPExportButton from "Components/PDFExporter/SVPExportButton";
import AuditReportPDF from "Pages/Audits/AuditReportPDF";
import {downloadPdf, previewPdf} from "Components/PDFExporter/PDFExporterUtils";
import SVPNavigationSpeedDial from "Components/SpeedDial/SVPNavigationSpeedDial";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faMapLocationDot, faUserPen} from "@fortawesome/free-solid-svg-icons";
import {Info as InfoIcon} from "@mui/icons-material";
import PropTypes from "prop-types";
import {AuditShareButton} from "Pages/Audits/Components/AuditShareButton";
import {AuditTitle} from "Pages/Audits/Components/auditTitle";
import {
    AnnotationType,
    AuditItemTypeInitialFilters,
    AuditStatusID,
    AuditStatusNames,
} from "../../Models/Audit/AuditConstants";
import {AuditsStatusSelectStyle} from "./Audits.styles";
import {GSPSSubmitButton} from "../../Components/Common/GSPSSubmitButton";
import {hasUserUrlPermission} from "../../Models/User/UserUtils";
import {UserUrlsPermissionsID} from "../../Models/User/UserConstants";
import {useCurrentUser} from "../../Models/User/useCurrentUser";

export const AuditViewer = ({publicAudit = null}) => {
    const currentUser = useCurrentUser();
    const {auditID} = useParams();
    const [audit, setAudit] = useState();
    const [searchParams] = useSearchParams();
    const [reviewer, setReviewer] = useState([]);
    const [QuesAns, setQuesAns] = useState([]);
    const [annotationMarkers, setAnnotationMarkers] = useState([]);
    const [annotationLines, setAnnotationLines] = useState([]);
    const [annotationAreas, setAnnotationAreas] = useState([]);
    const [currentLocation, setCurrenLocation] = useState(null);
    const [clickedItem, setClickedItem] = useState(null);
    const [modalItemType, setModalItemType] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [issuesSeveritiesWithColor, setIssuesSeveritiesWithColor] = useState();
    const [isExportNotes, setIsExportNotes] = useState(false);
    const [mapImageForExportPDF, setMapImageForExportPDF] = useState(null);
    const [auditsFilter, setAuditsFilter] = useState(AuditItemTypeInitialFilters);
    const [completedAuditStatusSelection, setCompletedAuditStatusSelection] = useState(AuditStatusID.COMPLETED);
    const navigate = useNavigate();

    const mapRef = useRef();

    const iconStyle = {fontSize: "18px"};

    const actions = [
        {
            icon: <FontAwesomeIcon icon={faUserPen} style={iconStyle} />,
            name: "Reviewer Information",
            href: "reviewer",
        },
        {
            icon: <InfoIcon style={iconStyle} />,
            name: "Inspector Information",
            href: "audit-info",
        },
        !!auditID && {
            icon: <FontAwesomeIcon icon={faMapLocationDot} style={iconStyle} />,
            name: "Map",
            href: "map-container",
        },
    ].filter(Boolean);

    const hideEmailPopupFromURL = searchParams.get("report") === "false";
    const endpointToUseAudit = publicAudit || API_AUDITS.audits;
    const isPublicAudit = !!publicAudit;
    const [isShowSendReport, setIsShowSendReport] = useState(!(isPublicAudit || hideEmailPopupFromURL));

    const captureMap = () => {
        if (mapRef.current === null) {
            return;
        }

        html2canvas(mapRef.current, {
            scale: 2,
            logging: false,
            useCORS: true,
            allowTaint: true,
        })
            .then((canvas) => {
                const dataUrl = canvas.toDataURL("image/png");
                setMapImageForExportPDF(dataUrl);
            })
            .catch((err) => {
                console.error("Failed to capture map as image", err);
            });
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            captureMap();
        }, 3000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapRef.current]);

    function getAuditReportPDF() {
        return <AuditReportPDF
            audit={audit}
            mapImage={mapImageForExportPDF}
            canExportNotes={isExportNotes}
        />;
    }

    const exportOptions = [
        {
            onClick: () => {
                mapImageForExportPDF &&
                    previewPdf(getAuditReportPDF());
            },
            text: "Preview PDF",
        },
        {
            onClick: () => {
                mapImageForExportPDF &&
                    downloadPdf(
                        getAuditReportPDF(),
                        "SiteVisionPro - Report.pdf",
                    );
            },
            text: "Download PDF",
        },
    ];

    useEffect(() => {
        makeGetRequest(`${endpointToUseAudit}${auditID}/`).then(
            (res) => {
                setAudit(res);
                setReviewer([res.reviewer]);
                setQuesAns(res.answers.map((ans) => ans.answer_text));
                setQuestions(res.answers.map((ans) => ans.question_text));
                setCompletedAuditStatusSelection(res.audit_status.id);
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isPublicAudit) {
            makeGetRequest(`${API_AUDITS.issuesSeverities}`).then(
                (res) => {
                    setIssuesSeveritiesWithColor(res.results);
                },
            );
        }
    }, [isPublicAudit]);

    useEffect(() => {
        if (audit) {
            if (audit.audit_issues) {
                const issues = audit.audit_issues.map((issue) => ({
                    ...issue,
                    images: issue.issue_images.length > 0 ? issue.issue_images : [
                        initMediaSelector(AnnotationType.ISSUE, annotationMarkers.length, "0"),
                        initMediaSelector(AnnotationType.ISSUE, annotationMarkers.length, "1"),
                    ],
                    videos: issue.issue_videos.length > 0 ? issue.issue_videos :
                        [initMediaSelector(AnnotationType.ISSUE, annotationMarkers.length, "0")],
                }));
                setAnnotationMarkers(issues);
            }
            if (audit.audit_areas) {
                const areas = audit.audit_areas.map((area) => ({
                    ...area,
                    images: area.area_images.length > 0 ? area.area_images : [
                        initMediaSelector("area", annotationAreas.length, "0"),
                        initMediaSelector("area", annotationAreas.length, "1"),
                    ],
                    videos: area.area_videos.length > 0 ? area.area_videos :
                        [initMediaSelector("area", annotationAreas.length, "0")],
                }));
                setAnnotationAreas(areas);
            }
            if (audit.audit_lines) {
                const lines = audit.audit_lines.map((line) => ({
                    ...line,
                    images: line.line_images.length > 0 ? line.line_images : [
                        initMediaSelector("line", annotationLines.length, "0"),
                        initMediaSelector("line", annotationLines.length, "1"),
                    ],
                    videos: line.line_videos.length > 0 ? line.line_videos :
                        [initMediaSelector("line", annotationLines.length, "0")],
                }));
                setAnnotationLines(lines);
            }
        }
        if (audit?.location?.address) {
            setCurrenLocation({
                "lat": Number(audit?.location?.address.latitude),
                "lng": Number(audit?.location?.address.longitude),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audit]);

    const onLineClick = (line) => {
        setModalItemType("line");
        setClickedItem(line);
        setIsModalOpen(true);
    };

    const onAreaClick = (Area) => {
        setModalItemType("area");
        setClickedItem(Area);
        setIsModalOpen(true);
    };
    const onIssueClick = (issue) => {
        setModalItemType(AnnotationType.ISSUE);
        setClickedItem(issue);
        setIsModalOpen(true);
    };

    const tidySelectedIssues = useMemo(() => {
        return filterAndIndexItems(annotationMarkers, "pins", auditsFilter, true);
    }, [annotationMarkers, auditsFilter]);

    const tidySelectedLines = useMemo(() => {
        return filterAndIndexItems(annotationLines, "lines", auditsFilter);
    }, [annotationLines, auditsFilter]);

    const tidySelectedAreas = useMemo(() => {
        return filterAndIndexItems(annotationAreas, "areas", auditsFilter);
    }, [annotationAreas, auditsFilter]);


    const handleAuditStatusChange = (event, newValue) => {
        setCompletedAuditStatusSelection(newValue);
    };

    function renderUpdateAuditStatus() {
        return <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                marginBottom: "5px",
            }}
        >
            <Select
                labelId="tab-select-label"
                id="tab-select"
                value={completedAuditStatusSelection}
                variant="standard"
                onChange={(e) => handleAuditStatusChange(null, e.target.value)}
                displayEmpty={true}
                disableUnderline={true}
                sx={AuditsStatusSelectStyle}
            >
                <MenuItem value={AuditStatusID.COMPLETED} key={AuditStatusID.COMPLETED}>
                    <em>Update Inspection</em>
                </MenuItem>
                {Object.values(AuditStatusNames).map((tab) => (
                    <MenuItem key={tab.id} value={tab.id}>
                        {tab.name}
                    </MenuItem>
                ))}
            </Select>
            <GSPSSubmitButton
                onClick={() => updateInspectionStatus()}
                isDisabled={completedAuditStatusSelection === AuditStatusID.COMPLETED}
            />
        </div>;
    }

    function updateInspectionStatus() {
        const data = {audit_status_id: completedAuditStatusSelection};
        makePatchRequest(`${API_AUDITS.audits}${audit.id}/`, data).then(() => {
            navigate(`/audits/creator?auditId=${audit.id}`);
        });
    }

    return (
        <AuditContainer>
            {isShowSendReport && <EmailPopup isShowModal={isShowSendReport} setIsShowModal={setIsShowSendReport} />}
            {audit &&
                <>
                    <GSPSBreadCrumbs staticCrumb={`Inspection: ${audit.name}`} />
                    <div className="container">
                        <div className="intro my-4">
                            <AuditTitle audit={audit} />
                            <h6 className="subTitle subtitle-sm">
                                <strong>Account: </strong> {audit.account.name}. <span />
                                <strong>Project ID: </strong>
                                {audit.project_id ? audit.project_id : "-"}. <span />
                                <strong>Description: </strong>
                                {audit.description_type ? audit.description_type.name : "-"}. <span />
                            </h6>
                            <h6 className="subTitle subtitle-sm">
                                <strong>Audited By </strong>
                                {audit.auditor.first_name} {audit.auditor.last_name}. <span />
                                <strong>Reviewed By </strong> {audit.reviewer.first_name} {audit.reviewer.last_name}.
                            </h6>
                        </div>
                        <div className="intro my-4">
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    flexWrap: "wrap",
                                }}
                            >
                                <div style={{minWidth: "120px", marginBottom: "0px", marginLeft: "-10px"}}>
                                    <SVPMapFilter
                                        annotationMarkers={annotationMarkers}
                                        annotationLines={annotationLines}
                                        annotationAreas={annotationAreas}
                                        auditIssues={audit?.audit_issues}
                                        auditLines={audit?.audit_lines}
                                        auditAreas={audit?.audit_areas}
                                        auditsFilter={auditsFilter}
                                        setAuditsFilter={setAuditsFilter}
                                    />
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "10px",
                                        flexWrap: "wrap",
                                        justifyContent: "flex-end",
                                        marginTop: "8px",
                                    }}
                                >
                                    <AuditShareButton auditID={audit.id} />
                                    <SVPExportButton
                                        options={exportOptions}
                                        isLoading={!mapImageForExportPDF}
                                        isExportNotes={isExportNotes}
                                        setIsExportNotes={setIsExportNotes}
                                        isPublicAudit={isPublicAudit}
                                    />
                                </div>
                                {!isPublicAudit &&
                                    audit.audit_status.id === AuditStatusID.COMPLETED &&
                                    currentUser &&
                                    hasUserUrlPermission(currentUser, UserUrlsPermissionsID.EDIT_COMPLETED_AUDITS) &&
                                    renderUpdateAuditStatus()
                                }
                            </div>
                            <div ref={mapRef}>
                                <GSPSGoogleMap
                                    audit={audit}
                                    currentLocation={currentLocation}
                                    annotationAreas={annotationAreas}
                                    annotationLines={annotationLines}
                                    annotationMarkers={annotationMarkers}
                                    onLineClick={onLineClick}
                                    onIssueClick={onIssueClick}
                                    onAreaClick={onAreaClick}
                                    isViewOnly={true}
                                    auditsFilter={auditsFilter}
                                />
                            </div>
                            <SVPNavigationSpeedDial actions={actions} />
                            {clickedItem &&
                                <GSPSAnnotationModal
                                    isModalOpen={isModalOpen}
                                    onModalClose={() => setIsModalOpen(false)}
                                    audit={audit}
                                    clickedItem={clickedItem}
                                    annotationType={modalItemType}
                                    issuesSeverities={issuesSeveritiesWithColor}
                                    isViewOnly={true}
                                    isPublicAudit={isPublicAudit}
                                />
                            }
                            {auditsFilter.pins &&
                                auditsFilter.pins.length > 0 &&
                                auditsFilter.severity && auditsFilter.severity.length > 0 &&
                                <AuditMapSelectedIssues
                                    audit={audit}
                                    tidySelectedIssues={tidySelectedIssues}
                                    isViewOnly={true}
                                    isPublicAudit={isPublicAudit}
                                />
                            }
                            {auditsFilter.lines && auditsFilter.lines.length > 0 &&
                                <AuditMapSelectedLines
                                    tidySelectedLines={tidySelectedLines}
                                    isViewOnly={true}
                                    updateElementImages={null}
                                    updateElementVideos={null}
                                    isPublicAudit={isPublicAudit}
                                />
                            }
                            {auditsFilter.areas && auditsFilter.areas.length > 0 &&
                                <AuditMapSelectedAreas
                                    tidySelectedAreas={tidySelectedAreas}
                                    isViewOnly={true}
                                    isPublicAudit={isPublicAudit}
                                />
                            }
                            <AuditQuestions
                                questions={questions}
                                auditorAnswers={QuesAns}
                                isReadOnly={true}
                            />
                            <AuditorInformation
                                audit={audit}
                                isReadOnly={true}
                                isPublicAudit={isPublicAudit}
                            />
                            {reviewer.length > 0 &&
                                <div>
                                    <Divider className="my-4" />
                                    <ReviewerInformation
                                        audit={audit}
                                        users={reviewer}
                                        readonly={true}
                                        isPublicRecord={isPublicAudit}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                </>
            }
        </AuditContainer>
    );
};

AuditViewer.propTypes = {
    publicAudit: PropTypes.string,
};
